import React, { useEffect, useCallback, useMemo, useRef, useState } from 'react';
import {
  useNewsStore,
  useHeadlinesForOverlay,
  useFilteredCalendarEvents,
  useAvailableCountries,
  useNextEventIndex,
  isEventPast,
  isEventToday,
  isEventTomorrow,
  NewsHeadline,
  CalendarEvent,
  CalendarTimeFilter
} from '../store/newsStore';
import { usePreferences } from '../hooks/usePreferences';
import { useDraggable } from '../hooks/useDraggable';
import { useIsMobile } from '../hooks/useIsMobile';
import './NewsEventsOverlay.css';

// Country flag emoji mapping
const COUNTRY_FLAGS: Record<string, string> = {
  'US': '\u{1F1FA}\u{1F1F8}',
  'USA': '\u{1F1FA}\u{1F1F8}',
  'United States': '\u{1F1FA}\u{1F1F8}',
  'UK': '\u{1F1EC}\u{1F1E7}',
  'GB': '\u{1F1EC}\u{1F1E7}',
  'United Kingdom': '\u{1F1EC}\u{1F1E7}',
  'EU': '\u{1F1EA}\u{1F1FA}',
  'Eurozone': '\u{1F1EA}\u{1F1FA}',
  'Germany': '\u{1F1E9}\u{1F1EA}',
  'DE': '\u{1F1E9}\u{1F1EA}',
  'France': '\u{1F1EB}\u{1F1F7}',
  'FR': '\u{1F1EB}\u{1F1F7}',
  'Japan': '\u{1F1EF}\u{1F1F5}',
  'JP': '\u{1F1EF}\u{1F1F5}',
  'China': '\u{1F1E8}\u{1F1F3}',
  'CN': '\u{1F1E8}\u{1F1F3}',
  'Canada': '\u{1F1E8}\u{1F1E6}',
  'CA': '\u{1F1E8}\u{1F1E6}',
  'Australia': '\u{1F1E6}\u{1F1FA}',
  'AU': '\u{1F1E6}\u{1F1FA}',
  'Switzerland': '\u{1F1E8}\u{1F1ED}',
  'CH': '\u{1F1E8}\u{1F1ED}',
  'New Zealand': '\u{1F1F3}\u{1F1FF}',
  'NZ': '\u{1F1F3}\u{1F1FF}',
  'Italy': '\u{1F1EE}\u{1F1F9}',
  'Spain': '\u{1F1EA}\u{1F1F8}',
  'Netherlands': '\u{1F1F3}\u{1F1F1}',
  'Sweden': '\u{1F1F8}\u{1F1EA}',
  'Norway': '\u{1F1F3}\u{1F1F4}',
  'Denmark': '\u{1F1E9}\u{1F1F0}',
  'Finland': '\u{1F1EB}\u{1F1EE}',
  'Austria': '\u{1F1E6}\u{1F1F9}',
  'Belgium': '\u{1F1E7}\u{1F1EA}',
  'Ireland': '\u{1F1EE}\u{1F1EA}',
  'Portugal': '\u{1F1F5}\u{1F1F9}',
  'Greece': '\u{1F1EC}\u{1F1F7}',
  'Poland': '\u{1F1F5}\u{1F1F1}',
  'Czech Republic': '\u{1F1E8}\u{1F1FF}',
  'Hungary': '\u{1F1ED}\u{1F1FA}',
  'Russia': '\u{1F1F7}\u{1F1FA}',
  'India': '\u{1F1EE}\u{1F1F3}',
  'South Korea': '\u{1F1F0}\u{1F1F7}',
  'Other': '\u{1F30D}',
};

const getCountryFlag = (country: string): string => {
  return COUNTRY_FLAGS[country] || '\u{1F30D}';
};

// Format relative time
const formatRelativeTime = (timestamp: number): string => {
  const now = Date.now();
  const diff = now - timestamp;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (seconds < 60) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return new Date(timestamp).toLocaleDateString();
};

// Format event time for calendar - always show in Eastern Time (ET)
const formatEventTime = (time: string, timestamp: number): string => {
  // If server already provided a formatted time string, use it (assumed to be PT)
  if (time) return `${time} PT`;
  // Otherwise format the timestamp in PT
  const formatted = new Date(timestamp).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'America/Los_Angeles',
  });
  return `${formatted} PT`;
};

// Format day abbreviation in Eastern Time
const formatDayAbbrev = (timestamp: number): string => {
  return new Date(timestamp).toLocaleDateString('en-US', {
    weekday: 'short',
    timeZone: 'America/Los_Angeles',
  });
};

// Get date string in Eastern Time for comparison
const getETDateString = (timestamp: number): string => {
  return new Date(timestamp).toLocaleDateString('en-US', {
    timeZone: 'America/Los_Angeles',
  });
};

// Format event date for display (shows day and date for non-today events) - uses PT
const formatEventDate = (timestamp: number): { day: string; date: string } | null => {
  const eventDateStr = getETDateString(timestamp);
  const todayStr = getETDateString(Date.now());
  const tomorrowTs = Date.now() + 24 * 60 * 60 * 1000;
  const tomorrowStr = getETDateString(tomorrowTs);

  // If today, don't show date badge (group header says "Today")
  if (eventDateStr === todayStr) {
    return null;
  }

  // If tomorrow, show day name
  if (eventDateStr === tomorrowStr) {
    return { day: 'Tomorrow', date: '' };
  }

  // Otherwise show day abbrev and date in PT
  const eventDate = new Date(timestamp);
  const month = eventDate.toLocaleDateString('en-US', { month: 'numeric', timeZone: 'America/Los_Angeles' });
  const day = eventDate.toLocaleDateString('en-US', { day: 'numeric', timeZone: 'America/Los_Angeles' });

  return {
    day: formatDayAbbrev(timestamp),
    date: `${month}/${day}`
  };
};

// Get time until event
const getTimeUntil = (timestamp: number): string => {
  const now = Date.now();
  const diff = timestamp - now;
  if (diff < 0) return 'Released';
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  if (hours > 0) return `in ${hours}h ${minutes % 60}m`;
  return `in ${minutes}m`;
};

// Close icon SVG
const CloseIcon: React.FC = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// Filter icon SVG
const FilterIcon: React.FC = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);

// Chevron icon
const ChevronIcon: React.FC<{ expanded?: boolean }> = ({ expanded }) => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

// Importance badge component
const ImportanceBadge: React.FC<{ importance: 'high' | 'medium' | 'low' }> = ({ importance }) => {
  const colors = {
    high: { bg: 'rgba(239, 68, 68, 0.2)', text: '#ef4444', label: 'HIGH' },
    medium: { bg: 'rgba(245, 158, 11, 0.2)', text: '#f59e0b', label: 'MED' },
    low: { bg: 'rgba(34, 197, 94, 0.2)', text: '#22c55e', label: 'LOW' },
  };
  const c = colors[importance];
  return (
    <span className="importance-badge" style={{ background: c.bg, color: c.text }}>
      {c.label}
    </span>
  );
};

// News headline item
const HeadlineItem: React.FC<{ headline: NewsHeadline }> = ({ headline }) => {
  const isBreaking = headline.category === 'BREAKING' || headline.importance === 'high';
  return (
    <div className={`news-item ${isBreaking ? 'breaking' : ''}`}>
      <div className="news-item-header">
        {isBreaking ? (
          <span className="breaking-badge">BREAKING</span>
        ) : (
          headline.importance && <ImportanceBadge importance={headline.importance} />
        )}
        <span className="news-time">{formatRelativeTime(headline.timestamp)}</span>
        {headline.source === 'x' ? (
          <span className="news-source news-source-x">X {headline.category}</span>
        ) : (
          headline.source && <span className="news-source">{headline.source}</span>
        )}
      </div>
      <div className="news-headline">{headline.headline}</div>
    </div>
  );
};

// Calendar event item
const CalendarEventItem: React.FC<{ event: CalendarEvent; isNext?: boolean; showDate?: boolean }> = ({ event, isNext, showDate = false }) => {
  const isPast = isEventPast(event);
  const dateInfo = showDate ? formatEventDate(event.timestamp) : null;
  const isEarnings = event.category === 'earnings';

  return (
    <div
      className={`calendar-item ${isPast ? 'past' : ''} ${isNext ? 'next-event' : ''} ${event.importance}${isEarnings ? ' earnings' : ''}`}
      data-event-id={event.id}
    >
      <div className="calendar-item-left">
        {dateInfo && (
          <div className="calendar-date-badge">
            <span className="date-day">{dateInfo.day}</span>
            {dateInfo.date && <span className="date-num">{dateInfo.date}</span>}
          </div>
        )}
        <span className="calendar-time-badge">
          {formatEventTime(event.time, event.timestamp)}
        </span>
        {!isPast && (
          <span className="time-until">{getTimeUntil(event.timestamp)}</span>
        )}
      </div>
      <div className="calendar-item-content">
        <div className="calendar-item-header">
          {isEarnings ? (
            <>
              <span className="calendar-earnings-icon" aria-label="earnings">{'\u{1F4CA}'}</span>
              {event.ticker && <span className="calendar-earnings-ticker">{event.ticker}</span>}
            </>
          ) : (
            <>
              <span className="calendar-flag">{getCountryFlag(event.country)}</span>
              <span className="calendar-country">{event.country}</span>
            </>
          )}
          <ImportanceBadge importance={event.importance} />
        </div>
        <div className="calendar-event-name">{event.event}</div>
        {(event.actual || event.forecast || event.previous) && (
          <div className="calendar-values">
            {event.actual && (
              <span className="calendar-value actual">
                <span className="label">{isEarnings ? 'EPS:' : 'A:'}</span>
                <span className="value">{event.actual}{event.unit || ''}</span>
              </span>
            )}
            {event.forecast && (
              <span className="calendar-value forecast">
                <span className="label">{isEarnings ? 'Est:' : 'F:'}</span>
                <span className="value">{event.forecast}{event.unit || ''}</span>
              </span>
            )}
            {event.previous && (
              <span className="calendar-value previous">
                <span className="label">P:</span>
                <span className="value">{event.previous}{event.unit || ''}</span>
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Filter dropdown component
const FilterDropdown: React.FC<{
  label: string;
  options: { value: string; label: string; icon?: string }[];
  selected: string[];
  onChange: (values: string[]) => void;
  multiSelect?: boolean;
}> = ({ label, options, selected, onChange, multiSelect = true }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const toggleOption = (value: string) => {
    if (multiSelect) {
      if (selected.includes(value)) {
        onChange(selected.filter(v => v !== value));
      } else {
        onChange([...selected, value]);
      }
    } else {
      onChange([value]);
      setIsOpen(false);
    }
  };

  const activeCount = selected.length;
  const hasFilter = activeCount > 0 && activeCount < options.length;

  return (
    <div className="filter-dropdown" ref={dropdownRef}>
      <button
        className={`filter-btn ${hasFilter ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <FilterIcon />
        <span>{label}</span>
        {hasFilter && <span className="filter-count">{activeCount}</span>}
        <ChevronIcon expanded={isOpen} />
      </button>
      {isOpen && (
        <div className="filter-menu">
          {options.map(opt => (
            <label key={opt.value} className="filter-option">
              <input
                type="checkbox"
                checked={selected.includes(opt.value)}
                onChange={() => toggleOption(opt.value)}
              />
              {opt.icon && <span className="filter-icon">{opt.icon}</span>}
              <span>{opt.label}</span>
            </label>
          ))}
          <div className="filter-actions">
            <button onClick={() => onChange(options.map(o => o.value))}>All</button>
            <button onClick={() => onChange([])}>None</button>
          </div>
        </div>
      )}
    </div>
  );
};

// Group events by date
const groupEventsByDate = (events: CalendarEvent[]): { today: CalendarEvent[]; tomorrow: CalendarEvent[]; later: CalendarEvent[] } => {
  const today: CalendarEvent[] = [];
  const tomorrow: CalendarEvent[] = [];
  const later: CalendarEvent[] = [];

  events.forEach(event => {
    if (isEventToday(event)) {
      today.push(event);
    } else if (isEventTomorrow(event)) {
      tomorrow.push(event);
    } else {
      later.push(event);
    }
  });

  return { today, tomorrow, later };
};

// Main overlay component
const NewsEventsOverlay: React.FC = () => {
  const isMobile = useIsMobile();
  const overlayRef = useRef<HTMLDivElement>(null);
  const calendarListRef = useRef<HTMLDivElement>(null);
  const { preferences, updatePreference, isLoading } = usePreferences();

  // Get news panel preferences
  const newsPrefs = preferences.newsPanel || {};
  const hasSyncedFromPrefs = useRef(false);

  // Store state
  const isVisible = useNewsStore((state) => state.isOverlayVisible);
  const activeTab = useNewsStore((state) => state.activeTab);
  const connectionStatus = useNewsStore((state) => state.connectionStatus);
  const setOverlayVisible = useNewsStore((state) => state.setOverlayVisible);
  const setActiveTab = useNewsStore((state) => state.setActiveTab);

  // On desktop, show as sidebar when visible (toggled by NEWS button in ticker)
  const showSidebar = !isMobile;
  const timeFilter = useNewsStore((state) => state.calendarTimeFilter);
  const importanceFilter = useNewsStore((state) => state.calendarImportanceFilter);
  const countryFilter = useNewsStore((state) => state.calendarCountryFilter);
  const setTimeFilter = useNewsStore((state) => state.setCalendarTimeFilter);
  const setImportanceFilter = useNewsStore((state) => state.setCalendarImportanceFilter);
  const setCountryFilter = useNewsStore((state) => state.setCalendarCountryFilter);

  // Data selectors
  const headlines = useHeadlinesForOverlay();
  const calendarEvents = useFilteredCalendarEvents();
  const availableCountries = useAvailableCountries();
  const nextEventIndex = useNextEventIndex();

  // Group calendar events
  const groupedEvents = useMemo(() => groupEventsByDate(calendarEvents), [calendarEvents]);

  // Initialize from preferences (wait for preferences to load)
  useEffect(() => {
    if (isLoading || hasSyncedFromPrefs.current) return;
    hasSyncedFromPrefs.current = true;

    if (newsPrefs.visible !== undefined) {
      setOverlayVisible(newsPrefs.visible);
    }
    if (newsPrefs.activeTab) {
      setActiveTab(newsPrefs.activeTab);
    }
    if (newsPrefs.calendarFilters?.timeRange) {
      setTimeFilter(newsPrefs.calendarFilters.timeRange);
    }
    if (newsPrefs.calendarFilters?.importance) {
      setImportanceFilter(newsPrefs.calendarFilters.importance);
    }
    if (newsPrefs.calendarFilters?.countries) {
      setCountryFilter(newsPrefs.calendarFilters.countries);
    }
  }, [isLoading, newsPrefs]);

  // Save visibility to preferences when it changes (skip initial render)
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    updatePreference('newsPanel', { visible: isVisible });
  }, [isVisible, updatePreference]);

  // Default position
  const defaultPosition = isMobile
    ? { x: 10, y: 60 }
    : { x: window.innerWidth - 420, y: 100 };

  // Draggable functionality - uses preferenceKey for automatic position persistence
  const { position, dragHandlers } = useDraggable(overlayRef, {
    defaultPosition: newsPrefs.position || defaultPosition,
    mobileDefaultPosition: newsPrefs.mobilePosition || { x: 10, y: 60 },
    preferenceKey: 'newsPanel',
    constrainToViewport: true,
    excludeSelectors: ['button', '.news-tab', '.filter-dropdown', '.filter-menu', 'input'],
  });

  // Track if we've done the initial auto-scroll for this "session"
  const hasAutoScrolled = useRef(false);

  // Reset auto-scroll flag when tab or visibility changes
  useEffect(() => {
    hasAutoScrolled.current = false;
  }, [isVisible, activeTab]);

  // Auto-scroll to next event ONLY on initial view (not on data changes)
  useEffect(() => {
    if (!isVisible || activeTab !== 'calendar' || calendarEvents.length === 0) return;
    if (hasAutoScrolled.current) return; // Don't scroll if user may have scrolled manually

    // Small delay to ensure DOM is ready after visibility change
    const timeoutId = setTimeout(() => {
      if (!calendarListRef.current || hasAutoScrolled.current) return;

      const nextEvent = calendarEvents[nextEventIndex];
      if (nextEvent) {
        const eventEl = calendarListRef.current.querySelector(`[data-event-id="${nextEvent.id}"]`);
        if (eventEl) {
          eventEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          hasAutoScrolled.current = true;
        }
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [isVisible, activeTab, nextEventIndex, calendarEvents]);

  // Handle close
  const handleClose = useCallback(() => {
    setOverlayVisible(false);
  }, [setOverlayVisible]);

  // Handle tab change with preference save
  const handleTabChange = useCallback((tab: 'news' | 'calendar') => {
    setActiveTab(tab);
    updatePreference('newsPanel', { activeTab: tab });
  }, [setActiveTab, updatePreference]);

  // Handle filter changes with preference save
  const handleImportanceFilterChange = useCallback((values: string[]) => {
    const validValues = values.filter((v): v is 'high' | 'medium' | 'low' =>
      ['high', 'medium', 'low'].includes(v)
    );
    // If empty, default to all
    const allImportance: ('high' | 'medium' | 'low')[] = ['high', 'medium', 'low'];
    const finalValues = validValues.length > 0 ? validValues : allImportance;
    setImportanceFilter(finalValues);
    updatePreference('newsPanel', {
      calendarFilters: {
        ...newsPrefs.calendarFilters,
        importance: finalValues,
      }
    });
  }, [setImportanceFilter, updatePreference, newsPrefs.calendarFilters]);

  const handleCountryFilterChange = useCallback((values: string[]) => {
    setCountryFilter(values);
    updatePreference('newsPanel', {
      calendarFilters: {
        ...newsPrefs.calendarFilters,
        countries: values,
      }
    });
  }, [setCountryFilter, updatePreference, newsPrefs.calendarFilters]);

  const handleTimeFilterChange = useCallback((value: CalendarTimeFilter) => {
    setTimeFilter(value);
    updatePreference('newsPanel', {
      calendarFilters: {
        ...newsPrefs.calendarFilters,
        timeRange: value,
      }
    });
  }, [setTimeFilter, updatePreference, newsPrefs.calendarFilters]);

  // Time filter options
  const timeFilterOptions: { value: CalendarTimeFilter; label: string }[] = [
    { value: 'tomorrow', label: 'Till Tomorrow' },
    { value: 'week', label: 'This Week' },
  ];

  // Importance filter options
  const importanceOptions = [
    { value: 'high', label: 'High Impact' },
    { value: 'medium', label: 'Medium Impact' },
    { value: 'low', label: 'Low Impact' },
  ];

  // Country filter options
  const countryOptions = availableCountries.map(c => ({
    value: c,
    label: c,
    icon: getCountryFlag(c),
  }));

  // Count badges
  const upcomingHighImportance = calendarEvents.filter(e =>
    e.importance === 'high' && !isEventPast(e)
  ).length;

  // Hide when not visible (both desktop sidebar and mobile overlay)
  if (!isVisible) return null;

  // Mobile: render as modal with backdrop
  if (isMobile) {
    return (
      <div className="news-modal-backdrop" onClick={handleClose}>
        <div
          ref={overlayRef}
          className="news-overlay news-modal"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="news-overlay-header">
            <div className="news-overlay-title">
              <span className="news-icon">N</span>
              <span>Market News</span>
              <span className={`connection-dot ${connectionStatus}`} title={connectionStatus} />
            </div>
            <button className="news-close-btn" onClick={handleClose}>
              <CloseIcon />
            </button>
          </div>

          {/* Tabs */}
          <div className="news-overlay-tabs">
            <button
              className={`news-tab ${activeTab === 'news' ? 'active' : ''}`}
              onClick={() => handleTabChange('news')}
            >
              Headlines
              {headlines.length > 0 && <span className="tab-badge">{headlines.length}</span>}
            </button>
            <button
              className={`news-tab ${activeTab === 'calendar' ? 'active' : ''}`}
              onClick={() => handleTabChange('calendar')}
            >
              Calendar
              {upcomingHighImportance > 0 && (
                <span className="tab-badge high">{upcomingHighImportance}</span>
              )}
            </button>
          </div>

          {/* Filters for calendar */}
          {activeTab === 'calendar' && (
            <div className="news-filters">
              {/* Time range filter - segmented control */}
              <div className="time-filter-group">
                {timeFilterOptions.map(opt => (
                  <button
                    key={opt.value}
                    className={`time-filter-btn ${timeFilter === opt.value ? 'active' : ''}`}
                    onClick={() => handleTimeFilterChange(opt.value)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <FilterDropdown
                label="Impact"
                options={importanceOptions}
                selected={importanceFilter}
                onChange={handleImportanceFilterChange}
              />
              {countryOptions.length > 0 && (
                <FilterDropdown
                  label="Country"
                  options={countryOptions}
                  selected={countryFilter}
                  onChange={handleCountryFilterChange}
                />
              )}
            </div>
          )}

          {/* Content */}
          <div className="news-overlay-content">
            {activeTab === 'news' && (
              <div className="news-list">
                {headlines.length === 0 ? (
                  <div className="news-empty">
                    <div className="empty-icon">N</div>
                    <span>No headlines yet</span>
                    <span className="empty-hint">Headlines will appear here in real-time</span>
                  </div>
                ) : (
                  headlines.map((headline) => (
                    <HeadlineItem key={headline.id} headline={headline} />
                  ))
                )}
              </div>
            )}

            {activeTab === 'calendar' && (
              <div className="calendar-list" ref={calendarListRef}>
                {calendarEvents.length === 0 ? (
                  <div className="news-empty">
                    <div className="empty-icon">C</div>
                    <span>No calendar events</span>
                    <span className="empty-hint">Economic events will appear here</span>
                  </div>
                ) : (
                  <>
                    {groupedEvents.today.length > 0 && (
                      <div className="date-group">
                        <div className="date-header">Today</div>
                        {groupedEvents.today.map((event) => (
                          <CalendarEventItem
                            key={event.id}
                            event={event}
                            isNext={calendarEvents[nextEventIndex]?.id === event.id}
                          />
                        ))}
                      </div>
                    )}
                    {groupedEvents.tomorrow.length > 0 && (
                      <div className="date-group">
                        <div className="date-header">Tomorrow</div>
                        {groupedEvents.tomorrow.map((event) => (
                          <CalendarEventItem
                            key={event.id}
                            event={event}
                            isNext={calendarEvents[nextEventIndex]?.id === event.id}
                          />
                        ))}
                      </div>
                    )}
                    {groupedEvents.later.length > 0 && (
                      <div className="date-group">
                        <div className="date-header">This Week</div>
                        {groupedEvents.later.map((event) => (
                          <CalendarEventItem
                            key={event.id}
                            event={event}
                            isNext={calendarEvents[nextEventIndex]?.id === event.id}
                            showDate={true}
                          />
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Desktop: render as fixed sidebar
  return (
    <div
      ref={overlayRef}
      className="news-sidebar"
    >
      {/* Header */}
      <div className="news-overlay-header">
        <div className="news-overlay-title">
          <span className="news-icon">N</span>
          <span>Market News</span>
          <span className={`connection-dot ${connectionStatus}`} title={connectionStatus} />
        </div>
      </div>

      {/* Tabs */}
      <div className="news-overlay-tabs">
        <button
          className={`news-tab ${activeTab === 'news' ? 'active' : ''}`}
          onClick={() => handleTabChange('news')}
        >
          Headlines
          {headlines.length > 0 && <span className="tab-badge">{headlines.length}</span>}
        </button>
        <button
          className={`news-tab ${activeTab === 'calendar' ? 'active' : ''}`}
          onClick={() => handleTabChange('calendar')}
        >
          Calendar
          {upcomingHighImportance > 0 && (
            <span className="tab-badge high">{upcomingHighImportance}</span>
          )}
        </button>
      </div>

      {/* Filters for calendar */}
      {activeTab === 'calendar' && (
        <div className="news-filters">
          {/* Time range filter - segmented control */}
          <div className="time-filter-group">
            {timeFilterOptions.map(opt => (
              <button
                key={opt.value}
                className={`time-filter-btn ${timeFilter === opt.value ? 'active' : ''}`}
                onClick={() => handleTimeFilterChange(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <FilterDropdown
            label="Impact"
            options={importanceOptions}
            selected={importanceFilter}
            onChange={handleImportanceFilterChange}
          />
          {countryOptions.length > 0 && (
            <FilterDropdown
              label="Country"
              options={countryOptions}
              selected={countryFilter}
              onChange={handleCountryFilterChange}
            />
          )}
        </div>
      )}

      {/* Content */}
      <div className="news-overlay-content">
        {activeTab === 'news' && (
          <div className="news-list">
            {headlines.length === 0 ? (
              <div className="news-empty">
                <div className="empty-icon">N</div>
                <span>No headlines yet</span>
                <span className="empty-hint">Headlines will appear here in real-time</span>
              </div>
            ) : (
              headlines.map((headline) => (
                <HeadlineItem key={headline.id} headline={headline} />
              ))
            )}
          </div>
        )}

        {activeTab === 'calendar' && (
          <div className="calendar-list" ref={calendarListRef}>
            {calendarEvents.length === 0 ? (
              <div className="news-empty">
                <div className="empty-icon">C</div>
                <span>No calendar events</span>
                <span className="empty-hint">Economic events will appear here</span>
              </div>
            ) : (
              <>
                {groupedEvents.today.length > 0 && (
                  <div className="date-group">
                    <div className="date-header">Today</div>
                    {groupedEvents.today.map((event, idx) => (
                      <CalendarEventItem
                        key={event.id}
                        event={event}
                        isNext={calendarEvents[nextEventIndex]?.id === event.id}
                      />
                    ))}
                  </div>
                )}
                {groupedEvents.tomorrow.length > 0 && (
                  <div className="date-group">
                    <div className="date-header">Tomorrow</div>
                    {groupedEvents.tomorrow.map((event) => (
                      <CalendarEventItem
                        key={event.id}
                        event={event}
                        isNext={calendarEvents[nextEventIndex]?.id === event.id}
                      />
                    ))}
                  </div>
                )}
                {groupedEvents.later.length > 0 && (
                  <div className="date-group">
                    <div className="date-header">This Week</div>
                    {groupedEvents.later.map((event) => (
                      <CalendarEventItem
                        key={event.id}
                        event={event}
                        isNext={calendarEvents[nextEventIndex]?.id === event.id}
                        showDate={true}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsEventsOverlay;
